import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Portal } from '@mui/material';
import { keyframes } from '@mui/system';
import { AchievementBadge } from './achievement-badge.component';

/**
 * Keyframe animations for the achievement popup.
 */
const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.8) translateY(20px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.3); }
  50% { box-shadow: 0 0 40px rgba(255, 215, 0, 0.6); }
  100% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.3); }
`;

const confettiDrop = keyframes`
  0% { transform: translateY(-100%) rotate(0deg); opacity: 1; }
  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
`;

/**
 * Single confetti piece.
 */
const ConfettiPiece = ({ delay, left, color, size }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: `${left}%`,
      width: size,
      height: size,
      backgroundColor: color,
      borderRadius: size > 6 ? '2px' : '50%',
      animation: `${confettiDrop} ${2 + Math.random() * 2}s ease-in ${delay}s forwards`,
      pointerEvents: 'none',
    }}
  />
);

/**
 * Generate confetti pieces array.
 */
const generateConfetti = () => {
  const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#FF4444'];
  return Array.from({ length: 40 }, (_, i) => ({
    id: i,
    delay: Math.random() * 0.5,
    left: Math.random() * 100,
    color: colors[Math.floor(Math.random() * colors.length)],
    size: 4 + Math.random() * 8,
  }));
};

/**
 * Achievement unlock popup overlay.
 * Appears when a new achievement is earned.
 * Shows badge, name, description, confetti, and dismiss button.
 * Auto-dismisses after 5 seconds.
 *
 * @param {Object} props
 * @param {Object|null} props.achievement - The achievement to display.
 * @param {function} props.onDismiss - Called when the popup is dismissed.
 */
export const AchievementPopup = ({ achievement, onDismiss }) => {
  const [confetti] = useState(() => generateConfetti());

  useEffect(() => {
    if (!achievement) return;

    const timer = setTimeout(() => {
      onDismiss();
    }, 5000);

    return () => clearTimeout(timer);
  }, [achievement, onDismiss]);

  if (!achievement) return null;

  return (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          overflow: 'hidden',
        }}
        onClick={onDismiss}
        role="dialog"
        aria-label="Achievement unlocked"
      >
        {/* Confetti */}
        {confetti.map((piece) => (
          <ConfettiPiece key={piece.id} {...piece} />
        ))}

        {/* Achievement card */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            animation: `${fadeIn} 0.5s ease-out, ${glow} 2s ease-in-out infinite`,
            backgroundColor: 'background.paper',
            borderRadius: 4,
            p: 4,
            textAlign: 'center',
            maxWidth: 340,
            width: '90%',
            position: 'relative',
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              letterSpacing: 3,
              mb: 2,
              display: 'block',
            }}
          >
            Achievement Unlocked
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <AchievementBadge
              icon={achievement.icon}
              tier={achievement.tier}
              name={achievement.name}
              description={achievement.description}
              unlocked
            />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {achievement.name}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {achievement.description}
          </Typography>

          <Button
            variant="contained"
            onClick={onDismiss}
            sx={{
              borderRadius: '20px',
              px: 4,
              fontWeight: 700,
            }}
          >
            Awesome!
          </Button>
        </Box>
      </Box>
    </Portal>
  );
};

export default AchievementPopup;
